import { useState, useEffect } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';
import type { VersionedArtifact, SqlContent } from '../services/api';

interface DataSourceGroup {
  dataSource: string;
  artifacts: VersionedArtifact[];
  type: 'redshift' | 'postgresql';
}

export function McpServerBrowserWidget(_props: WidgetProps) {
  const { artifacts, loadArtifacts, selectArtifact } = useArtifactStore();
  const [expandedDs, setExpandedDs] = useState<string | null>(null);

  useEffect(() => { loadArtifacts('sql'); }, []);

  const groups: DataSourceGroup[] = [];
  const byDs = new Map<string, VersionedArtifact[]>();
  for (const a of artifacts) {
    const ds = a.dataSource || 'unknown';
    if (!byDs.has(ds)) byDs.set(ds, []);
    byDs.get(ds)!.push(a);
  }
  for (const [ds, arts] of byDs) {
    groups.push({
      dataSource: ds,
      artifacts: arts,
      type: ds.includes('redshift') || ds.includes('maxsense') ? 'redshift' : 'postgresql',
    });
  }
  groups.sort((a, b) => a.dataSource.localeCompare(b.dataSource));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0">
        <div className="text-xs text-tower-muted font-semibold">MCP Data Sources</div>
        <div className="text-[10px] text-tower-muted">{artifacts.length} tools across {groups.length} sources</div>
      </div>

      <div className="flex-1 overflow-y-auto p-1">
        {groups.length === 0 && (
          <div className="text-tower-muted text-xs p-3 text-center">No MCP artifacts found</div>
        )}
        {groups.map((g) => (
          <div key={g.dataSource} className="mb-1">
            <button
              onClick={() => setExpandedDs(expandedDs === g.dataSource ? null : g.dataSource)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-tower-border/30 rounded"
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${g.type === 'redshift' ? 'bg-warning' : 'bg-primary'}`} />
              <span className="text-tower-text font-semibold flex-1 text-left">{g.dataSource}</span>
              <span className="text-tower-muted">{g.artifacts.length} tools</span>
              <span className="text-tower-muted text-[10px]">{g.type === 'redshift' ? 'RS' : 'PG'}</span>
              <span className={`text-tower-muted transition-transform ${expandedDs === g.dataSource ? 'rotate-90' : ''}`}>▸</span>
            </button>

            {expandedDs === g.dataSource && (
              <div className="ml-4 border-l border-tower-border/30 pl-2">
                {g.artifacts.map((a) => {
                  const sqlContent = a.content.type === 'sql' ? a.content as SqlContent : null;
                  return (
                    <button
                      key={a.artifactId}
                      onClick={() => selectArtifact(a.artifactId)}
                      className="w-full text-left px-2 py-1 text-xs hover:bg-tower-border/20 rounded flex flex-col gap-0.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${a.isLive ? 'bg-success' : 'bg-warning'}`} />
                        <span className="text-tower-accent font-mono">{a.name}</span>
                        <span className="text-tower-muted ml-auto">v{a.version}</span>
                      </div>
                      {sqlContent && (
                        <div className="text-[10px] text-tower-muted pl-3.5">
                          {sqlContent.category} · {sqlContent.params.length} params · {sqlContent.tables.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="px-2 py-1 border-t border-tower-border/50 shrink-0">
        <div className="flex items-center gap-2 text-[10px] text-tower-muted">
          <span className="w-2 h-2 rounded-full bg-warning" /> Redshift
          <span className="w-2 h-2 rounded-full bg-primary ml-2" /> PostgreSQL
          <span className="w-2 h-2 rounded-full bg-success ml-2" /> Live
          <span className="w-2 h-2 rounded-full bg-warning ml-2" /> Draft
        </div>
      </div>
    </div>
  );
}
